<script>
import { app } from '@/main'
import { VMoney } from 'v-money'
import { mapState, mapActions } from 'vuex'
import { getStatusClass, getStatusText } from '@/support/utils/statusTagHelper'

const TRANSLATION = {
  headers: {
    payment: app.$t('finances.view:modal.headers.payment'),
    transaction: app.$t('finances.view:modal.headers.transaction'),
    summary: app.$t('finances.view:modal.headers.summary'),
    cart: app.$t('finances.view:modal.headers.cart')
  },

  labels: {
    createdAt: app.$t('finances.view:modal.labels.createdAt'),
    paymentDate: app.$t('finances.view:modal.labels.paymentDate'),
    paymentMethod: app.$t('finances.view:modal.labels.paymentMethod'),
    paymentInstallments: app.$t('finances.view:modal.labels.paymentInstallments'),
    totalValue: app.$t('finances.view:modal.labels.totalValue'),
    gateway: app.$t('finances.view:modal.labels.gateway'),
    value: app.$t('finances.view:modal.labels.value'),
    orderID: app.$t('finances.view:modal.labels.orderID'),
    paymentID: app.$t('finances.view:modal.labels.paymentID'),
    status: app.$t('finances.view:modal.labels.status'),
    statusFailedDescription: app.$t('finances.view:modal.labels.statusFailedDescription')
  },

  list: {
    credit_card: app.$t('finances:list.credit_card'),
    boleto: app.$t('finances:list.boleto')
  }
}

export default {
  name: 'ViewerPage',

  components: {
    Modal: () => import('@/components/general/Modal')
  },

  directives: { money: VMoney },

  data () {
    return {
      title: '',
      view: '',
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: false, // doesn't work with directive
        allowBlank: true
      },

      cieloStatusMap: {
        0: {
          status: 'Falha ao processar o pagamento',
          detailedStatus:
            'Possíveis causas: dados incorretos, erro na requisição, timeout do adquirente, alguma instabilidade no processamento. Em caso de transação de débito, o comprador pode ter abandonado a compra.'
        },

        1: {
          status: 'Meio de pagamento apto a ser capturado ou pago',
          detailedStatus:
            'Para transação de boleto, significa que o boleto foi gerado com sucesso. Para transação com cartão, significa que houve a aprovação pelo banco emissor. Contudo, isso não significa que a transação foi concluída - para isso, é necessária uma segunda etapa, a captura da transação ou a efetivação do pagamento.'
        },

        2: {
          status: 'Pagamento confirmado e finalizado',
          detailedStatus: 'Pagamento confirmado e finalizado.'
        },

        3: {
          status: 'Pagamento negado por autorizador',
          detailedStatus:
            'Possíveis causas: limite insuficiente, falha de processamento do cartão, bandeira indisponível, bloqueio por fraude, entre outros.'
        },

        10: {
          status: 'Pagamento cancelado',
          detailedStatus:
            'É a suspensão da transação, isentando de taxa ou valores cobrados. As transações pré-autorizadas podem ser canceladas mesmo após as 23h59 da data de autorização. Já as transações capturadas podem ser canceladas até as 23h59 do mesmo dia da autorização, após esse horário o valor será estornado.'
        },

        11: {
          status: 'Pagamento cancelado/estornado',
          detailedStatus:
            'Significa que foi solicitado o cancelamento da transação, podendo ocorrer a partir das 0h00 do dia após a criação da transação. Isso pode acontecer por conta de dados incorretos ou por solicitação do comprador.'
        },

        12: {
          status: 'Esperando retorno da instituição financeira',
          detailedStatus:
            'Significa que a transação foi enviada em processo de pré-autorização, esperando uma resposta do banco emissor para validá-la.'
        },

        13: {
          status: 'Pagamento cancelado por falha no processamento',
          detailedStatus:
            'Significa que a transação foi cancelada por falha de processamento. Também pode ser abortada, caso o Antifraude negue a transação antes da autorização.'
        },

        20: {
          status: 'Recorrência agendada',
          detailedStatus:
            'Significa que a transação terá uma recorrência agendada, ou seja, o valor da compra será recolhido no dia, ou no agendamento definido pela loja.'
        }
      }
    }
  },

  computed: {
    ...mapState([ 'fetching' ]),

    isStatusFinished () {
      return this.view.status && (this.view.status === 'finished' || this.view.status === 'paid')
    },

    isStatusFailed () {
      return this.view.status && this.view.status === 'failed'
    },

    canShowValues () {
      return this.isStatusFinished || this.isStatusFailed
    }
  },

  created () {
    this.setup()
    this.setFetching(true)

    Promise.all([ this.getFinanceById({ id: this.$route.params.id }) ]).finally(() => {
      this.setFetching(false)
    })
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback'
    ]),

    ...mapActions('FinanceModule', [ 'attemptGetFinanceById' ]),

    setup () {
      this.TRANSLATION = TRANSLATION
    },

    closeModal () {
      this.$router.push({ name: 'finance.list' })
    },

    getFinanceById () {
      this.attemptGetFinanceById(this.$route.params.id)
        .then((response) => {
          if (response.length === 0) return

          this.title = response.id
          this.view = response
        })
        .catch((err) => {
          console.error(err)

          this.setFeedback({ message: this.$t('finances.load:fetch.error') })
          this.closeModal()
        })
    },

    getStatusTagClass (status) {
      return getStatusClass(status)
    },

    getStatusTagText (status) {
      return getStatusText(status)
    },

    getStatusFailedDescription (order) {
      if (order?.data?.[0]?.message) {
        let message = order.data[0].message

        if (order.data[0].details?.[0]?.description) {
          message += ` - ${order.data[0].details[0].description}`
        }

        return {
          status: message || '-',
          detailedStatus: ''
        }
      }

      const cieloStatus = order?.data?.[0]?.Payment?.Status

      if (typeof cieloStatus === 'number') {
        const mapped = this.cieloStatusMap[cieloStatus]

        if (mapped) return mapped

        return {
          status: cieloStatus,
          detailedStatus: ''
        }
      }

      return {
        status: '-',
        detailedStatus: ''
      }
    },

    getLabelProduct (item) {
      return item.quantity + 'x - ' + (item.solution ? item.solution.name : item.program ? item.program.name : item.tool ? item.tool.name : '')
    },

    getPriceProduct (item) {
      return this.formatCurrency(item.price)
    }
  }
}
</script>

<template>
  <Modal
    :active="!fetching"
    :cancel-label="$t('global:back')"
    @close="closeModal"
  >
    <div
      v-if="view"
      class="modal-form modal-view-finance"
    >
      <span class="modal-subtitle">{{ $t("finances.view:modal.subtitle") }}</span>
      <h2 class="modal-title">
        {{ formatTransactionId(view.id) }}
      </h2>
      <div class="modal-status">
        <span
          class="status-tag"
          :class="getStatusTagClass(view.status)"
        >{{ getStatusTagText(view.status) }}</span>
      </div>
      <div class="finance-view">
        <div class="finance-view-body">
          <div class="view-body view-body--left">
            <h2 class="view-header">
              {{ TRANSLATION.headers.payment }}
            </h2>
            <div class="view-body-content">
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.createdAt }}
                </div>
                <div class="item-value">
                  {{ formatDateTime(view.createdAt) }}
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.paymentDate }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  {{ formatDateTime(view.paymentDate) }}
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.paymentMethod }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  {{ TRANSLATION.list[view.paymentMethod] }}
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.paymentInstallments }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  {{ view.products && view.products.length > 0 ? formatInstallments(view.products[0].data) : "" }}
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.totalValue }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  <div class="money-value">
                    {{ formatCurrency(view.value) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="view-body view-body--right">
            <h2 class="view-header">
              {{ TRANSLATION.headers.transaction }}
            </h2>
            <div class="view-body-content">
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.gateway }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  {{ view.providers[0].provider.name }}
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.value }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  <div class="money-value">
                    {{ formatCurrency(view.value) }}
                  </div>
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.orderID }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  {{ view.id }}
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.paymentID }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  {{ view.transactionId }}
                </div>
              </div>
              <div class="item">
                <div class="item-label">
                  {{ TRANSLATION.labels.status }}
                </div>
                <div
                  v-if="canShowValues"
                  class="item-value"
                >
                  {{ getStatusTagText(view.status) }}
                </div>
              </div>
              <div
                v-if="view.status === 'failed'"
                class="item with-tooltip"
              >
                <div class="item-label">
                  {{ TRANSLATION.labels.statusFailedDescription }}
                </div>
                <div class="item-value">
                  {{ getStatusFailedDescription(view).status }}

                  <template v-if="getStatusFailedDescription(view).detailedStatus">
                    <svg
                      v-tooltip="{
                        content: getStatusFailedDescription(view).detailedStatus,
                        placement: 'bottom-start',
                        classes: ['info', 'status-tooltip'],
                        offset: 5,
                      }"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#ffffff"
                      class="bi bi-question-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                      <path
                        d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286m1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94"
                      />
                    </svg>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="finance-view-footer">
          <h2 class="view-header">
            {{ canShowValues ? TRANSLATION.headers.summary : TRANSLATION.headers.cart }}
          </h2>
          <div class="view-body">
            <ul>
              <li
                v-for="(summaryItem, summaryItemIndex) in view.products"
                :key="summaryItemIndex + ':' + summaryItem.id"
                class="summary-item"
              >
                <div class="summary-item--solution">
                  {{ getLabelProduct(summaryItem) }}
                </div>
                <div
                  v-if="summaryItem.solution || summaryItem.program || summaryItem.tool"
                  class="summary-item--value"
                >
                  <div class="money-value text-right">
                    {{ getPriceProduct(summaryItem) }}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" src="../assets/scss/Viewer.scss"></style>

<style>
.tooltip {
  color: #005eb8;
  font-size: 0.95rem;
  font-weight: 500;
  z-index: 999;
}

.tooltip[aria-hidden="true"] {
  visibility: unset;
  opacity: 0;
  transition: opacity 0.15s, visibility 0.15s;
}

.tooltip[aria-hidden="false"] {
  visibility: unset;
  opacity: 1;
  transition: opacity 0.15s;
}

.modal-view-finance {
  .item-value {
    display: flex;
    align-items: center;
  }

  .has-tooltip {
    margin: 0px 8px;
    cursor: pointer;
  }
}

.tooltip.info .tooltip-inner,
.popover-inner div {
  color: #ffffff !important;
  background: linear-gradient(to bottom, #005eb8 0%, #001b5c 400%);
  padding: 12px;
  border-radius: 5px;
  max-width: 500px;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
